/**
 * L1 §3 — the 30-day success rate, per site and per SLA tier.
 *
 * The panel next door (`sla-section.tsx`) answers "is each tier green *right
 * now*", which is last night's news. This one answers the question management
 * and an audit actually ask: **are we meeting 98% over the last thirty days.**
 * Nothing new is collected for it — `backup_job_run` already holds thirteen
 * months of per-run history, deliberately deeper than Azure's own ~14 days, and
 * this is a read of it.
 *
 * Form (design rules §2): a ratio against a limit, compared across categories —
 * a horizontal bar per site/tier with a dashed `ReferenceLine` at the target, so
 * "who is under" is answerable without reading a number. Status colours only
 * (met / breached / unknown), because meeting an SLA is a status and not a
 * series, and the icon legend under each chart keeps it from being colour-alone
 * (§3.2). Every bar drills into the filtered job list (§4.1).
 *
 * Three honesty rules it exists to hold (§5):
 *
 * - **Restores are out of the maths.** Azure reports them through the same job
 *   feed as backups, so a failed restore test would otherwise push a site under
 *   target. The backend excludes them and publishes what it excluded; the
 *   caption says so where it happened.
 * - **A rate with nothing decided is a gap, not 0%.** `success_rate_pct` comes
 *   back null and no bar is drawn — the category keeps its axis label, so the
 *   site stays visible without being libelled at 0%.
 * - **Unread is never green.** The panel greys out on `readable === false` *or*
 *   a non-empty `errors[]`, because a failed store read produces no breaches
 *   either, and an empty list of breaches must not read as full compliance.
 *
 * The SQL transaction-log card below the two scope charts answers a different
 * question against a different number, and it is deliberately not folded into
 * them. Veeam runs the log backup as a child of a normal backup job and grades
 * it **separately**, so a parent job can read Success all month while its log
 * chain degrades — and the log chain is what makes a point-in-time restore
 * possible. It also runs every few minutes against a job that runs once, so
 * merged into the site rate a handful of log jobs would simply become the
 * estate's rate. Its target is 90%, not 98%, for the same operational reason.
 */

import { Chart } from "@norican/dataviz";
import { Icon } from "@norican/icons";
import { Alert, DataGrid, EmptyState, Meter, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { BackupSlaCompliance, BackupSlaRow, BackupSqlLogRow } from "@/lib/api";
import { formatCount, formatPercent, formatRelativeTime } from "@/lib/charts/config";
import { useDrillNavigate } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { BACKUP_JOBS_PATH, SITE_PARAM, SLA_PARAM } from "./params";
import { formatTimestamp, statusStyle } from "./status";

/** One series, so no legend (§6) — the card title names it. */
const RATE_CONFIG = {
  success_rate_pct: { label: "Success rate", color: statusStyle("success").color },
};

/** Same single series, named for the chain it measures. */
const SQL_RATE_CONFIG = {
  success_rate_pct: { label: "Log success rate", color: statusStyle("success").color },
};

/**
 * The backend's catch-all buckets. Neither is a filter *value*: a job with no
 * site or no SLA tier stores NULL, so drilling on the literal would land on an
 * empty list. The drill drops the dimension instead, which is the honest
 * equivalent — the same thing `sla-section` does for its `Untiered` bar.
 */
const CATCH_ALL_KEYS = new Set(["Untiered", "UNKNOWN"]);

interface SlaComplianceSectionProps {
  compliance: BackupSlaCompliance;
  /** Source filter in force. Rates are aggregated server-side across both
   *  sources, so this cannot narrow them — it is used to say so, not to lie. */
  sourceIds: string[];
  /** Site filter in force. Hiding rows of a per-site aggregate is sound, so
   *  the site chart honours it; a tier spans sites, so the tier chart cannot. */
  siteIds: string[];
}

export function SlaComplianceSection({
  compliance,
  sourceIds,
  siteIds,
}: SlaComplianceSectionProps) {
  /**
   * The switch that greys the whole panel. `readable` is false when the run
   * history could not be read; `errors` is non-empty when any read behind this
   * payload failed. Either means the rates describe less than the window
   * claims, and a rate over unread data must never render as compliance.
   */
  const unknown = !compliance.readable || compliance.errors.length > 0;

  const sites =
    siteIds.length === 0
      ? compliance.sites
      : compliance.sites.filter((row) => siteIds.includes(row.key));

  return (
    <section id="backup-sla-30d" className="scroll-mt-6">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <SlaScopeCard
          twinId="backup-sla-30d-sites"
          title={`Success rate by site · ${compliance.days} days`}
          noun="site"
          header="Site"
          param={SITE_PARAM}
          rows={sites}
          compliance={compliance}
          unknown={unknown}
          sourceIds={sourceIds}
        />
        <SlaScopeCard
          twinId="backup-sla-30d-tiers"
          title={`Success rate by SLA tier · ${compliance.days} days`}
          noun="tier"
          header="Tier"
          param={SLA_PARAM}
          rows={compliance.tiers}
          compliance={compliance}
          unknown={unknown}
          sourceIds={sourceIds}
        />
      </div>
      <div className="mt-4">
        <SqlLogCard compliance={compliance} unknown={unknown} />
      </div>
    </section>
  );
}

/**
 * The transaction-log check. Its own card, its own target, and deliberately not
 * a bar chart: there are a handful of log chains, not a category axis worth
 * charting, and what matters is one ratio against one limit — which is a
 * `Meter` (design rules §2).
 */
function SqlLogCard({
  compliance,
  unknown,
}: {
  compliance: BackupSlaCompliance;
  unknown: boolean;
}) {
  const sql = compliance.sql_log;
  if (!sql) {
    return null;
  }

  // Three states, and only one of them draws a number. `readable === false`
  // means the collector has never reported a log session — an older collector,
  // or a mode with no per-session view. That is not a failing chain, and 0%
  // would libel one.
  const measurable = !unknown && sql.readable && sql.success_rate_pct != null;
  const rate = sql.success_rate_pct ?? 0;

  return (
    <PanelCard
      title={`SQL transaction-log backups · ${compliance.days} days`}
      caption={
        measurable
          ? `${formatPercent(sql.success_rate_pct)} of ${formatCount(sql.decided)} decided session(s), target ${sql.target_pct}%`
          : "No rate — see below"
      }
    >
      <div className="flex flex-col gap-5">
        {!sql.readable && !unknown && (
          <Alert tone="neutral" title="No transaction-log session on record">
            {sql.status_detail}
          </Alert>
        )}
        {measurable && sql.meets_target === false && (
          <Alert
            tone="danger"
            title={`The log chain is at ${formatPercent(sql.success_rate_pct)}, under the ${sql.target_pct}% target`}
          >
            A transaction-log chain is what makes a point-in-time restore possible, and Veeam grades
            it separately from the job that owns it — so the parent job can be green while this is
            not. A warning counts against the target here, exactly as it does for a nightly backup.
          </Alert>
        )}

        {measurable ? (
          <Meter.Root
            value={rate}
            min={0}
            max={100}
            size="md"
            tone={sql.meets_target ? "success" : "danger"}
            aria-valuetext={`${rate.toFixed(1)} per cent of decided transaction-log sessions succeeded, against a ${sql.target_pct} per cent target`}
          >
            <div className="mb-1 flex items-baseline justify-between gap-3">
              <Meter.Label>
                <Typography variant="caption" tone="muted">
                  Succeeded, of the sessions with an outcome
                </Typography>
              </Meter.Label>
              <Meter.Value>
                {() => (
                  <Typography variant="caption" tone="muted" tabular>
                    {rate.toFixed(1)}% / {sql.target_pct}%
                  </Typography>
                )}
              </Meter.Value>
            </div>
            <Meter.Track>
              <Meter.Indicator />
            </Meter.Track>
          </Meter.Root>
        ) : (
          <Typography variant="body-sm" tone="muted">
            {unknown
              ? "The run history could not be read, so no transaction-log rate is shown."
              : sql.status_detail}
          </Typography>
        )}

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Statistic label="Succeeded" value={formatCount(sql.succeeded)} />
          <Statistic
            label="Warning"
            value={formatCount(sql.warning)}
            caption="Completed, needed attention — not counted as meeting the target"
          />
          <Statistic label="Failed" value={formatCount(sql.failed)} />
          <Statistic
            label="Still running"
            value={formatCount(sql.undecided)}
            caption="No outcome yet, so outside the rate"
          />
        </div>

        {sql.jobs.length > 0 && (
          <>
            {/* Same grammar as the two scope charts above: a ratio against a
                limit, compared across categories, with the target as a dashed
                line so "which chain is under" is the picture rather than a
                number to read. One chain at 50% among three at 100% is the
                whole finding, and a table makes you compute that. */}
            <Chart.Container
              config={SQL_RATE_CONFIG}
              className="h-56"
              aria-label={`Transaction-log success rate per parent job over ${compliance.days} days, against the ${sql.target_pct} percent target`}
            >
              <Chart.BarChart
                data={sql.jobs}
                layout="vertical"
                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
                <Chart.XAxis {...Chart.axisProps} type="number" domain={[0, 100]} unit="%" />
                <Chart.YAxis {...Chart.axisProps} type="category" dataKey="job" width={150} />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={<Chart.TooltipContent />}
                />
                <Chart.ReferenceLine x={sql.target_pct} {...Chart.nowMarker} />
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="success_rate_pct"
                  radius={[0, 3, 3, 0]}
                >
                  {sql.jobs.map((chain) => (
                    <Chart.Cell key={`${chain.source}:${chain.job}`} fill={chainFill(chain)} />
                  ))}
                </Chart.Bar>
              </Chart.BarChart>
            </Chart.Container>

            {/* Never colour-alone (§3.2). */}
            <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
              <LegendItem
                status="success"
                label="At target"
                count={sql.jobs.filter((chain) => chain.meets_target === true).length}
              />
              <LegendItem
                status="failed"
                label="Below target"
                count={sql.jobs.filter((chain) => chain.meets_target === false).length}
              />
              <LegendItem
                status="unknown"
                label="No decided session"
                count={sql.jobs.filter((chain) => chain.meets_target == null).length}
              />
            </div>
          </>
        )}

        {sql.jobs.length > 0 && (
          <DataGrid<BackupSqlLogRow>
            data={sql.jobs}
            getRowId={(row) => `${row.source}:${row.job}`}
            density="compact"
            aria-label="Transaction-log success rate per parent backup job"
            columns={[
              { id: "job", header: "Parent job", accessor: (row) => row.job },
              { id: "site", header: "Site", accessor: (row) => row.site ?? "—" },
              {
                id: "rate",
                header: "Rate",
                accessor: (row) => row.success_rate_pct ?? -1,
                align: "end",
                sortable: true,
                cell: (row) => (
                  <span className="tabular-nums">
                    {row.success_rate_pct == null ? "—" : formatPercent(row.success_rate_pct)}
                  </span>
                ),
              },
              {
                id: "sessions",
                header: "Decided",
                accessor: (row) => row.decided,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.decided)}</span>,
              },
              {
                id: "warning",
                header: "Warning",
                accessor: (row) => row.warning,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.warning)}</span>,
              },
              {
                id: "failed",
                header: "Failed",
                accessor: (row) => row.failed,
                align: "end",
                cell: (row) => <span className="tabular-nums">{formatCount(row.failed)}</span>,
              },
              {
                id: "last",
                header: "Last success",
                accessor: (row) => row.last_success ?? "",
                cell: (row) => (
                  <span className="text-fg-muted text-xs">
                    {row.last_success ? formatRelativeTime(row.last_success) : "Never"}
                  </span>
                ),
              },
            ]}
          />
        )}
      </div>
    </PanelCard>
  );
}

interface SlaScopeCardProps {
  twinId: string;
  title: string;
  noun: string;
  header: string;
  param: string;
  rows: BackupSlaRow[];
  compliance: BackupSlaCompliance;
  unknown: boolean;
  sourceIds: string[];
}

function SlaScopeCard({
  twinId,
  title,
  noun,
  header,
  param,
  rows,
  compliance,
  unknown,
  sourceIds,
}: SlaScopeCardProps) {
  const navigateDrill = useDrillNavigate();

  const breaching = rows.filter((row) => row.meets_target === false);
  const unrated = rows.filter((row) => row.meets_target == null);
  const meeting = rows.length - breaching.length - unrated.length;
  const restoreTested = rows.reduce((sum, row) => sum + row.restore_tested_jobs, 0);
  const excluded = rows.reduce((sum, row) => sum + row.restore_runs + row.non_backup_runs, 0);
  const newestRestore = rows.reduce<string | null>(
    (newest, row) =>
      row.last_restore != null && (newest == null || row.last_restore > newest)
        ? row.last_restore
        : newest,
    null,
  );

  return (
    <ChartCard<BackupSlaRow>
      title={title}
      caption={
        unknown
          ? "Unknown — the history behind this could not be read in full"
          : `Target ${compliance.target_pct}% · ${formatCount(breaching.length)} of ${formatCount(
              rows.length,
            )} below it`
      }
      since={compliance.collected_at}
      tableTwin={{
        id: twinId,
        ariaLabel: `Success rate per ${noun} over ${compliance.days} days`,
        data: rows,
        getRowId: (row) => row.key,
        columns: [
          { id: "key", header, accessor: (row) => row.key },
          { id: "jobs", header: "Jobs", accessor: (row) => row.jobs, align: "end" },
          { id: "runs", header: "Backup runs", accessor: (row) => row.runs, align: "end" },
          { id: "ok", header: "Succeeded", accessor: (row) => row.succeeded, align: "end" },
          { id: "warn", header: "Warnings", accessor: (row) => row.warning, align: "end" },
          { id: "failed", header: "Failed", accessor: (row) => row.failed, align: "end" },
          {
            id: "undecided",
            header: "No outcome",
            accessor: (row) => row.undecided,
            align: "end",
          },
          {
            id: "rate",
            header: "Success rate",
            // `formatPercent` renders null as an em dash, never as "0.0%" —
            // nothing decided is an unknown rate, not a total failure.
            accessor: (row) => row.success_rate_pct ?? -1,
            align: "end",
            cell: (row) => (
              <span className="text-sm tabular-nums">{formatPercent(row.success_rate_pct)}</span>
            ),
          },
          {
            id: "met",
            header: `Meets ${compliance.target_pct}%`,
            accessor: (row) => String(row.meets_target),
            cell: (row) => (
              <span className="text-sm">
                {row.meets_target == null ? "Unknown" : row.meets_target ? "Yes" : "No"}
              </span>
            ),
          },
          {
            id: "restore",
            header: "Restore tested",
            accessor: (row) => row.restore_tested_jobs,
            align: "end",
          },
          {
            id: "lastrestore",
            header: "Last restore",
            accessor: (row) => row.last_restore ?? "",
            cell: (row) => (
              <span className="text-sm">
                {row.last_restore ? formatTimestamp(row.last_restore) : "Never proven"}
              </span>
            ),
          },
        ],
      }}
    >
      {unknown ? (
        <EmptyState size="sm" title="No rate can be shown" description={compliance.status_detail} />
      ) : rows.length === 0 ? (
        <EmptyState
          size="sm"
          title={`No ${noun} has a session on record yet`}
          description="Compliance accrues from the first collection onward, and the history is then kept 13 months."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <Chart.Container
            config={RATE_CONFIG}
            className="h-96"
            aria-label={`Backup success rate per ${noun} over ${compliance.days} days, against the ${compliance.target_pct} percent target`}
          >
            <Chart.BarChart
              data={rows}
              layout="vertical"
              margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
            >
              <Chart.CartesianGrid {...Chart.gridProps} horizontal={false} />
              <Chart.XAxis {...Chart.axisProps} type="number" domain={[0, 100]} unit="%" />
              <Chart.YAxis {...Chart.axisProps} type="category" dataKey="key" width={88} />
              <Chart.Tooltip
                cursor={{ fill: "var(--norican-surface-hover)" }}
                content={<Chart.TooltipContent />}
              />
              {/* The target, in the kit's dashed-divider styling. Without it the
                  bars are lengths; with it, "who is under" is the picture. */}
              <Chart.ReferenceLine x={compliance.target_pct} {...Chart.nowMarker} />
              <Chart.Bar
                {...Chart.seriesDefaults}
                dataKey="success_rate_pct"
                radius={[0, 3, 3, 0]}
                cursor="pointer"
                onClick={(item: { payload?: BackupSlaRow }) => {
                  const key = item.payload?.key;
                  if (key) {
                    navigateDrill(
                      BACKUP_JOBS_PATH,
                      CATCH_ALL_KEYS.has(key) ? {} : { [param]: key },
                    );
                  }
                }}
              >
                {rows.map((row) => (
                  <Chart.Cell key={row.key} fill={rateFill(row)} />
                ))}
              </Chart.Bar>
            </Chart.BarChart>
          </Chart.Container>

          {/* Status is never colour-alone — the three tones are named with their
              icons and counts, which is also the only place a row with no rate
              is visible, since there is no bar to draw for it. */}
          <div className="text-fg-muted flex flex-wrap justify-center gap-3 text-xs">
            <LegendItem status="success" label="Meets target" count={meeting} />
            <LegendItem status="failed" label="Below target" count={breaching.length} />
            <LegendItem status="unknown" label="No decided session" count={unrated.length} />
          </div>

          <Typography variant="body-sm" tone="muted">
            {excluded > 0
              ? `${formatCount(excluded)} non-backup session(s) — restores and policy changes, which Azure reports through the same job feed — are excluded from these rates. `
              : ""}
            {restoreTested > 0
              ? `${formatCount(restoreTested)} job(s) proven by a successful restore${
                  newestRestore ? `, most recently ${formatRelativeTime(newestRestore)}` : ""
                }.`
              : "Nothing here has been proven by a successful restore — the only evidence a backup can be recovered from."}
            {compliance.stale
              ? " A source has been quiet, so the newest part of the window is missing and these rates cover less than the full period."
              : ""}
            {sourceIds.length > 0
              ? " The rates are aggregated across both sources, so the source filter above does not narrow them."
              : ""}
          </Typography>
        </div>
      )}
    </ChartCard>
  );
}

/**
 * Fill for one bar. Met / breached / unknown is a **status** scale, so it wears
 * the same three tokens the badges do rather than a categorical ramp — read
 * from `status.ts`, so this panel cannot disagree with the grids.
 */
function chainFill(chain: BackupSqlLogRow): string {
  if (chain.meets_target == null) {
    return statusStyle("unknown").color;
  }
  return chain.meets_target ? statusStyle("success").color : statusStyle("failed").color;
}

function rateFill(row: BackupSlaRow): string {
  if (row.meets_target == null) {
    return statusStyle("unknown").color;
  }
  return row.meets_target ? statusStyle("success").color : statusStyle("failed").color;
}

/** One legend entry: icon, label and count — never colour on its own (§3.2). */
function LegendItem({ status, label, count }: { status: string; label: string; count: number }) {
  const style = statusStyle(status);
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon icon={style.icon} size="xs" />
      {label} ({formatCount(count)})
    </span>
  );
}
